import React, { useMemo, useState } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import { debounce } from 'lodash';
import {
  Alert,
  Box,
  Card,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import * as XLSX from 'xlsx';
import { GET_ALL_PAYMENTS, GET_PENDING_PAYMENTS } from './queries';
import {
  PAYMENT_COLUMNS,
  PENDING_COLUMNS,
  fmtMoney,
  type Column,
  type Payment,
  type PendingPayment,
} from './columns';

const RED = '#E60012';

const headerCellSx = {
  backgroundColor: RED,
  color: '#fff',
  fontWeight: 700,
  fontSize: { xs: '0.72rem', sm: '0.8rem' },
  whiteSpace: 'nowrap',
  padding: { xs: '8px', sm: '10px 12px' },
  borderRight: '1px solid rgba(255,255,255,0.18)',
  position: 'sticky' as const,
  top: 0,
  zIndex: 11,
};

const cellSx = {
  padding: { xs: '6px 8px', sm: '8px 12px' },
  fontSize: { xs: '0.74rem', sm: '0.8rem' },
  borderRight: '1px solid #eee',
  verticalAlign: 'top' as const,
};

/**
 * One table body, shared by both tabs.
 *
 * Both tabs render from the same column definitions, so a column added in
 * columns.ts appears in the table and the export together rather than in
 * whichever one someone remembered to update.
 */
function DataTable<T>({
  columns,
  rows,
  loading,
  emptyText,
  startIndex,
  rowKey,
  rowSx,
}: {
  columns: Column<T>[];
  rows: T[];
  loading: boolean;
  emptyText: string;
  startIndex: number;
  rowKey: (row: T, i: number) => string;
  rowSx?: (row: T) => object;
}) {
  const totalCols = columns.length + 1;
  return (
    <TableContainer
      component={Paper}
      sx={{ flex: 1, minHeight: 0, overflow: 'auto', boxShadow: 'none', borderRadius: 0 }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell align="center" sx={{ ...headerCellSx, minWidth: 64 }}>
              Sr.No
            </TableCell>
            {columns.map((c) => (
              <TableCell
                key={c.label}
                align="center"
                sx={{ ...headerCellSx, minWidth: c.width ?? 140 }}
              >
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={totalCols} align="center" sx={{ py: 8, border: 0 }}>
                <CircularProgress size={28} sx={{ color: RED }} />
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={totalCols} align="center" sx={{ py: 8, color: '#888', border: 0 }}>
                <Stack alignItems="center" spacing={1}>
                  <CreditCardIcon sx={{ fontSize: '2.5rem', color: '#ccc' }} />
                  <Typography variant="body2" color="text.secondary">
                    {emptyText}
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, idx) => (
              <TableRow
                key={rowKey(row, idx)}
                hover
                sx={{
                  backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa',
                  ...(rowSx ? rowSx(row) : {}),
                }}
              >
                <TableCell align="center" sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                  {startIndex + idx + 1}
                </TableCell>
                {columns.map((c) => {
                  const raw = c.value(row);
                  const text =
                    c.money && typeof raw === 'number'
                      ? fmtMoney(raw)
                      : raw === null || raw === undefined || raw === ''
                        ? '—'
                        : String(raw);
                  const isBlank = text === '—';
                  return (
                    <TableCell
                      key={c.label}
                      align={c.align ?? 'left'}
                      sx={{
                        ...cellSx,
                        minWidth: c.width ?? 140,
                        maxWidth: c.wrap ? c.width ?? 240 : undefined,
                        whiteSpace: c.wrap ? 'normal' : 'nowrap',
                        color: isBlank ? '#bbb' : undefined,
                        fontWeight: c.money ? 600 : undefined,
                      }}
                    >
                      {c.wrap ? (
                        text
                      ) : (
                        <Tooltip title={text.length > 24 ? text : ''} placement="top" arrow>
                          <Box
                            component="span"
                            sx={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {text}
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export const PaymentsDashboard: React.FC = () => {
  const [tab, setTab] = useState<'PAID' | 'PENDING'>('PAID');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [hideTests, setHideTests] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
        setPage(0);
      }, 500),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    debouncedSearch(e.target.value);
  };

  const switchTab = (next: 'PAID' | 'PENDING') => {
    setTab(next);
    setPage(0);
  };

  const variables = {
    limit: rowsPerPage,
    offset: page * rowsPerPage,
    searchTerm: searchTerm || null,
  };

  const paidQuery = useQuery(GET_ALL_PAYMENTS, {
    variables,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: tab !== 'PAID',
  });
  const pendingQuery = useQuery(GET_PENDING_PAYMENTS, {
    variables,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: tab !== 'PENDING',
  });

  const [fetchAllPaid] = useLazyQuery(GET_ALL_PAYMENTS);
  const [fetchAllPending] = useLazyQuery(GET_PENDING_PAYMENTS);

  const active = tab === 'PAID' ? paidQuery : pendingQuery;
  const response =
    tab === 'PAID' ? active.data?.getAllPayments : active.data?.getPendingPayments;
  const totalCount: number = response?.count ?? 0;

  const paidRows: Payment[] = tab === 'PAID' ? (response?.data ?? []) : [];
  const pendingRows: PendingPayment[] = tab === 'PENDING' ? (response?.data ?? []) : [];

  // A recharge is never Rs 1, so these are gateway pokes rather than sales.
  // Hidden by default but filtered client-side only, so the record count still
  // reflects what the bank actually sent and the two never silently disagree.
  const visiblePaid = hideTests ? paidRows.filter((r) => !r.isTestPayment) : paidRows;
  const hiddenTests = paidRows.length - visiblePaid.length;

  const handleExport = async () => {
    if (totalCount === 0) return;
    try {
      setIsExporting(true);
      const exportVars = { ...variables, limit: totalCount, offset: 0 };
      if (tab === 'PAID') {
        const { data } = await fetchAllPaid({ variables: exportVars, fetchPolicy: 'network-only' });
        const all: Payment[] = data?.getAllPayments?.data ?? [];
        const rows = hideTests ? all.filter((r) => !r.isTestPayment) : all;
        writeSheet(PAYMENT_COLUMNS, rows, 'Payments', 'payments');
      } else {
        const { data } = await fetchAllPending({ variables: exportVars, fetchPolicy: 'network-only' });
        const all: PendingPayment[] = data?.getPendingPayments?.data ?? [];
        writeSheet(PENDING_COLUMNS, all, 'Awaiting Payment', 'awaiting-payment');
      }
    } catch (err) {
      console.error('Failed to export:', err);
    } finally {
      setIsExporting(false);
    }
  };

  /** Money stays a number so Excel can sum it; only the table formats it. */
  function writeSheet<T>(columns: Column<T>[], rows: T[], sheetName: string, filePrefix: string) {
    const formatted = rows.map((row, index) => {
      const out: Record<string, string | number | null> = { 'Sr.No': index + 1 };
      for (const c of columns) {
        const v = c.value(row);
        out[c.label] = v === undefined ? '' : v;
      }
      return out;
    });
    const worksheet = XLSX.utils.json_to_sheet(formatted, {
      header: ['Sr.No', ...columns.map((c) => c.label)],
    });
    worksheet['!cols'] = [{ wch: 6 }, ...columns.map((c) => ({ wch: Math.round((c.width ?? 140) / 8) }))];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, `${filePrefix}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Page header */}
      <Box display="flex" alignItems="flex-start" gap={1.5} sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: '#FDECEC',
            flexShrink: 0,
          }}
        >
          <CreditCardIcon sx={{ color: RED, fontSize: '1.6rem' }} />
        </Box>
        <Box>
          <Typography variant="h5" fontSize={{ xs: '1.4rem', sm: '1.9rem' }} fontWeight={700}>
            Payments &amp; Transactions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Every customer payment, with the columns the invoice ledger carries.
          </Typography>
        </Box>
      </Box>

      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 2,
          borderRadius: 3,
          bgcolor: '#fff',
          height: 'calc(100vh - 170px)',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => switchTab(v)}
          sx={{
            flexShrink: 0,
            borderBottom: '1px solid #eee',
            px: 1,
            '& .MuiTab-root': { fontWeight: 700, textTransform: 'none' },
            '& .Mui-selected': { color: `${RED} !important` },
            '& .MuiTabs-indicator': { backgroundColor: RED },
          }}
        >
          <Tab value="PAID" label="Payments" icon={<CreditCardIcon fontSize="small" />} iconPosition="start" />
          <Tab
            value="PENDING"
            label="Awaiting Payment"
            icon={<HourglassEmptyIcon fontSize="small" />}
            iconPosition="start"
          />
        </Tabs>

        {/* Toolbar */}
        <Box
          sx={{
            flexShrink: 0,
            px: 2,
            pt: 2,
            pb: 1.5,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 1.5,
            alignItems: { md: 'center' },
            borderBottom: '1px solid #eee',
          }}
        >
          <TextField
            size="small"
            placeholder={
              tab === 'PAID'
                ? 'Search VIN, customer, dealer, session, cheque ref, IMEI...'
                : 'Search VIN, customer, dealer, session...'
            }
            value={searchInput}
            onChange={handleSearchChange}
            sx={{ flex: 1, minWidth: { xs: '100%', md: 300 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {tab === 'PAID' && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={hideTests}
                  onChange={(e) => setHideTests(e.target.checked)}
                  sx={{ '& .Mui-checked': { color: RED }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: RED } }}
                />
              }
              label={
                <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                  Hide ₹1 test payments{hiddenTests ? ` (${hiddenTests} on this page)` : ''}
                </Typography>
              }
            />
          )}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip
              label={`${totalCount} record${totalCount === 1 ? '' : 's'}`}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
            <Tooltip title="Export to Excel" placement="top" arrow>
              <span>
                <IconButton
                  onClick={handleExport}
                  disabled={isExporting || totalCount === 0}
                  sx={{ color: RED }}
                >
                  {isExporting ? <CircularProgress size={22} sx={{ color: RED }} /> : <CloudDownloadIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        {active.error && (
          <Box sx={{ px: 2, pt: 1.5 }}>
            <Alert severity="error" variant="outlined">
              Error loading {tab === 'PAID' ? 'payments' : 'pending sessions'}: {active.error.message}
            </Alert>
          </Box>
        )}
        {response && response.success === false && response.error && (
          <Box sx={{ px: 2, pt: 1.5 }}>
            <Alert severity="error" variant="outlined">
              {response.error}
            </Alert>
          </Box>
        )}

        {tab === 'PAID' ? (
          <DataTable
            columns={PAYMENT_COLUMNS}
            rows={visiblePaid}
            loading={active.loading}
            emptyText="No matching payments found."
            startIndex={page * rowsPerPage}
            rowKey={(r, i) => r.bankAlertRef || r.transactionId || String(i)}
            // A test poke that has been un-hidden should look unlike a sale.
            rowSx={(r) => (r.isTestPayment ? { backgroundColor: '#FFF8E1' } : {})}
          />
        ) : (
          <DataTable
            columns={PENDING_COLUMNS}
            rows={pendingRows}
            loading={active.loading}
            emptyText="Nothing is awaiting payment."
            startIndex={page * rowsPerPage}
            rowKey={(r, i) => r.sessionId || String(i)}
          />
        )}

        <Box sx={{ flexShrink: 0, borderTop: '1px solid #e0e0e0', overflowX: 'auto' }}>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Box>
      </Card>
    </Box>
  );
};
